import { useCallback, useEffect, useRef, useState } from "react";
import { getProgress, KanjiMemoryProgress, setProgress } from "../utils/kanjiMemoryStorage";
import { getCloudProgress, putCloudProgress } from "../utils/progressCloud";
import { useAuth } from "../context/AuthContext";
import { ProgressSyncStatus } from "./progressTypes";

const CLOUD_WRITE_DEBOUNCE_MS = 800;
const FEATURE_KEY = "kanjiMemory";

export function useKanjiMemoryProgress() {
    const { status, user } = useAuth();
    const [progress, setProgressState] = useState<KanjiMemoryProgress>(() => getProgress());
    const [syncStatus, setSyncStatus] = useState<ProgressSyncStatus>("local");
    const writeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestProgressRef = useRef(progress);
    latestProgressRef.current = progress;

    useEffect(() => {
        if (status !== "authenticated" || !user?.identityId) {
            setProgressState(getProgress());
            setSyncStatus("local");
            return;
        }

        let cancelled = false;
        setSyncStatus("loading");

        getCloudProgress(user.identityId, FEATURE_KEY)
            .then((remote) => {
                if (cancelled) return;
                if (remote?.data && Object.keys(remote.data).length > 0) {
                    setProgressState(remote.data);
                }
                setSyncStatus("ready");
            })
            .catch(() => {
                if (!cancelled) setSyncStatus("error");
            });

        return () => {
            cancelled = true;
        };
    }, [status, user?.identityId]);

    useEffect(() => {
        return () => {
            if (writeTimeoutRef.current !== null) clearTimeout(writeTimeoutRef.current);
        };
    }, []);

    const classify = useCallback(
        (symbol: string, status_: string) => {
            setProgressState((current) => {
                const next = { ...current, [symbol]: status_ };
                setProgress(next);

                if (status === "authenticated" && user?.identityId) {
                    if (writeTimeoutRef.current !== null) clearTimeout(writeTimeoutRef.current);
                    writeTimeoutRef.current = setTimeout(() => {
                        putCloudProgress(user.identityId, FEATURE_KEY, latestProgressRef.current).catch(() => {
                            setSyncStatus("error");
                        });
                    }, CLOUD_WRITE_DEBOUNCE_MS);
                }

                return next;
            });
        },
        [status, user?.identityId]
    );

    const reset = useCallback(() => {
        setProgress({});
        setProgressState({});
        if (writeTimeoutRef.current !== null) clearTimeout(writeTimeoutRef.current);

        if (status === "authenticated" && user?.identityId) {
            putCloudProgress(user.identityId, FEATURE_KEY, {}).catch(() => {
                setSyncStatus("error");
            });
        }
    }, [status, user?.identityId]);

    return { progress, classify, reset, syncStatus };
}
